import { Stack, Typography } from '@mui/material';
import { compact } from 'lodash';

import { EditButton } from '@/components/form/baseInputs/Button/EditButton';
import { UnstyledButton } from '@/components/form/baseInputs/Button/UnstyledButton';
import { UserPlus02Icon } from '@/components/icons/UserPlus02Icon';
import { Card, CardVariant } from '@/components/layout/Card/Card';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LoadingSkeletonOrValue } from '@/components/progress/LoadingSkeletonOrValue/LoadingSkeletonOrValue';
import { STATE_CODE_TO_NAME } from '@/constants/states';
import { useReportError } from '@/hooks/useReportError';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';

import { ClientPeopleListViewTable_ClientProfileFragment } from './graphql/ClientPeopleListViewTable.generated';

export interface ClientDetailsGrantorCardProps {
  grantor: ClientPeopleListViewTable_ClientProfileFragment | null | undefined;
  loading: boolean;
  onClick: (clientProfileId: string | null) => void;
}

const GRANTOR_CARD_HEIGHT_PX = 80;

export function ClientDetailsGrantorCard({
  grantor,
  loading,
  onClick,
}: ClientDetailsGrantorCardProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const canEdit = useCanEditHouseholdData();

  if (!grantor && !loading) {
    return (
      <UnstyledButton
        data-testid="AddSecondPersonButton"
        onClick={() => onClick(null)}
        sx={{ width: '100%', height: GRANTOR_CARD_HEIGHT_PX }}
        disabled={!canEdit}
      >
        <Card
          variant={canEdit ? 'outlined' : 'filled'}
          sx={{ width: '100%', height: '100%' }}
        >
          <Stack
            direction="row"
            width="100%"
            alignContent="center"
            justifyContent="center"
            alignItems="center"
            height="100%"
            spacing={1}
          >
            <UserPlus02Icon />
            <Typography variant="body-s">Add a second person</Typography>
          </Stack>
        </Card>
      </UnstyledButton>
    );
  }
  const variant: CardVariant = loading ? 'outlined' : 'filled';
  return (
    <>
      <Card
        variant={variant}
        sx={{ p: 2, height: GRANTOR_CARD_HEIGHT_PX }}
        data-testid="primary-client-card"
      >
        <Stack direction="row" width="100%" height="100%" alignItems="center">
          <Stack direction="column" flexGrow={2} spacing={0.5}>
            <Typography variant="title-s">
              <LoadingSkeletonOrValue
                loading={loading}
                value={grantor?.displayName}
              />
            </Typography>
            <Typography variant="body-xs">
              <LoadingSkeletonOrValue
                loading={loading}
                value={compact([
                  'Client',
                  grantor?.address?.stateCode
                    ? STATE_CODE_TO_NAME[
                        grantor.address.stateCode.toUpperCase()
                      ]
                    : undefined,
                ]).join(' | ')}
              />
            </Typography>
          </Stack>
          {canEdit && (
            <EditButton
              variant="transparent"
              aria-label="Edit client"
              size="sm"
              sx={{ maxHeight: '36px', maxWidth: '36px' }}
              disabled={loading}
              onClick={() => {
                if (!grantor?.id) {
                  showFeedback(
                    'Could not edit the client.  Please refresh and try again'
                  );
                  reportError(
                    'Attempted to edit a grantor without an ID',
                    new Error('attempted to edit a grantor without an ID'),
                    { grantor: JSON.stringify(grantor) }
                  );
                  return;
                }
                onClick(grantor.id);
              }}
            />
          )}
        </Stack>
      </Card>
    </>
  );
}
